import { useState } from "react";

type SaveResponse = {
  pk?: string;
  updated_at?: string;
};

type SavePayloadBuilder = (context: {
  existingTestPk: string;
  benchmark: string;
}) => Record<string, unknown>;

type UseAssessmentBuilderSaveOptions = {
  search: string;
  token: string | null;
  apiUrl: string | null | undefined;
  testType: "reading" | "listening";
  difficultyLevel: string;
  buildPayload: SavePayloadBuilder;
};

export function useAssessmentBuilderSave({
  search,
  token,
  apiUrl,
  testType,
  difficultyLevel,
  buildPayload,
}: UseAssessmentBuilderSaveOptions) {
  const [benchmark, setBenchmark] = useState<string>(
    () => new URLSearchParams(search).get("benchmark")?.trim() || "",
  );
  const [isDemo, setIsDemo] = useState<string>(
      () => new URLSearchParams(search).get("is_demo")?.trim() || "",
  );
  const [existingTestPk, setExistingTestPk] = useState<string>(
    () => new URLSearchParams(search).get("pk")?.trim() || "",
  );
  const [isSavingTest, setIsSavingTest] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const saveTest = async () => {
    if (!token) {
      setSaveError("Missing auth token.");
      return;
    }
    if (!apiUrl) {
      setSaveError("Assessments API URL is not configured.");
      return;
    }
    if (!difficultyLevel.trim()) {
      setSaveError("Difficulty level is required.");
      return;
    }
    if (!benchmark.trim()) {
      setSaveError("Benchmark is required.");
      return;
    }

    try {
      setIsSavingTest(true);
      setSaveError(null);
      setSaveMessage(null);

      const response = await fetch(`${apiUrl}/content/${testType}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          buildPayload({ existingTestPk, benchmark: benchmark.trim() }),
        ),
      });

      if (!response.ok) {
        setSaveError(`Unable to save test (${response.status})`);
        return;
      }

      const payload = (await response.json()) as SaveResponse;
      if (payload.pk) {
        setExistingTestPk(payload.pk);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("pk", payload.pk);
        currentUrl.searchParams.set("benchmark", benchmark.trim());
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      }

      if (payload.updated_at) {
        setSaveMessage(
          `Saved at ${new Date(payload.updated_at).toLocaleString()}`,
        );
      } else {
        setSaveMessage("Saved successfully.");
      }
    } catch (saveTestError: unknown) {
      setSaveError(
        saveTestError instanceof Error
          ? saveTestError.message
          : "Unable to save test.",
      );
    } finally {
      setIsSavingTest(false);
    }
  };

  return {
    benchmark,
    setBenchmark,
    isDemo,
    setIsDemo,
    existingTestPk,
    setExistingTestPk,
    isSavingTest,
    saveMessage,
    setSaveMessage,
    saveError,
    setSaveError,
    saveTest,
  };
}
